import React, { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { X, Plus, GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ActivityMetadataSheetProps {
  isOpen: boolean;
  onClose: () => void;
  activityName: string;
  activityDescription?: string;
  activityObjectives?: string[];
  onSave: (data: {
    name: string;
    description: string;
    objectives: string[];
  }) => void;
}

interface SortableObjectiveItemProps {
  id: string;
  objective: string;
  index: number;
  onRemove: (index: number) => void;
}

const SortableObjectiveItem: React.FC<SortableObjectiveItemProps> = ({
  id,
  objective,
  index,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded group"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <span className="text-primary font-bold">•</span>
      <span className="flex-1 text-sm text-gray-700 break-words">
        {objective}
      </span>
      <button
        onClick={() => onRemove(index)}
        className="p-1 text-gray-400 transition-opacity opacity-0 group-hover:opacity-100 hover:text-red-500"
        aria-label="Remove objective"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const ActivityMetadataSheet: React.FC<ActivityMetadataSheetProps> = ({
  isOpen,
  onClose,
  activityName,
  activityDescription = "",
  activityObjectives = [],
  onSave,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(activityName);
  const [description, setDescription] = useState(activityDescription);
  const [objectives, setObjectives] = useState<string[]>(activityObjectives);
  const [newObjective, setNewObjective] = useState("");

  // Track if user has made any changes
  const [hasUserChanges, setHasUserChanges] = useState(false);
  // Track the last saved values to detect when backend data changes
  const lastSavedValuesRef = useRef({ name: activityName, description: activityDescription, objectives: activityObjectives });

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update form only when:
  // 1. Sheet opens AND user has no unsaved changes, OR
  // 2. Backend data has changed (after a save)
  useEffect(() => {
    const backendDataChanged =
      lastSavedValuesRef.current.name !== activityName ||
      lastSavedValuesRef.current.description !== activityDescription ||
      JSON.stringify(lastSavedValuesRef.current.objectives) !== JSON.stringify(activityObjectives);

    if (isOpen && (!hasUserChanges || backendDataChanged)) {
      setName(activityName);
      setDescription(activityDescription);
      setObjectives(activityObjectives.length > 0 ? activityObjectives : []);
      setHasUserChanges(false);
      lastSavedValuesRef.current = { name: activityName, description: activityDescription, objectives: activityObjectives };
    }
  }, [isOpen, activityName, activityDescription, activityObjectives, hasUserChanges]);

  const handleAddObjective = () => {
    if (newObjective.trim()) {
      setObjectives([...objectives, newObjective.trim()]);
      setNewObjective("");
      setHasUserChanges(true);
    }
  };

  const handleRemoveObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
    setHasUserChanges(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setObjectives((items) => {
        const oldIndex = items.findIndex((_, i) => `objective-${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `objective-${i}` === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasUserChanges(true);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setHasUserChanges(true);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setHasUserChanges(true);
  };

  const handleSave = () => {
    onSave({
      name: name.trim(),
      description: description.trim(),
      objectives: objectives.filter((obj) => obj.trim()),
    });
    setHasUserChanges(false);
    lastSavedValuesRef.current = {
      name: name.trim(),
      description: description.trim(),
      objectives: objectives.filter((obj) => obj.trim()),
    };
    onClose();
  };

  const descriptionCharsLeft = 2000 - description.length;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-2xl">
            {t("activity_details") || "Activity Details"}
          </SheetTitle>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Activity Name */}
          <div className="space-y-2">
            <Label htmlFor="activity-name" className="text-sm font-semibold">
              {t("activity_name") || "Activity Name"}
              <span className="ml-1 text-red-500">*</span>
            </Label>
            <Input
              id="activity-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={t("enter_activity_name") || "Enter activity name"}
              className="w-full"
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold">
              {t("description") || "Description"}
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder={
                t("activity_description_placeholder") ||
                "Describe what this activity is about, what participants will learn, and how it helps them..."
              }
              className="w-full min-h-[120px] resize-y"
              maxLength={2000}
            />
            <div className="flex items-center justify-between text-xs">
              <span
                className={`${
                  descriptionCharsLeft < 100
                    ? "text-orange-600"
                    : "text-gray-500"
                }`}
              >
                {descriptionCharsLeft} {t("characters_left") || "characters left"}
              </span>
            </div>
          </div>

          {/* Learning Objectives */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">
              {t("learning_objectives") || "Learning Objectives"}
            </Label>

            {/* Add New Objective */}
            <div className="flex gap-2">
              <Input
                value={newObjective}
                onChange={(e) => setNewObjective(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddObjective();
                  }
                }}
                placeholder={
                  t("add_objective_placeholder") ||
                  "e.g., Identify personal decision-making patterns"
                }
                className="flex-1"
                maxLength={200}
              />
              <Button
                type="button"
                onClick={handleAddObjective}
                disabled={!newObjective.trim()}
                size="sm"
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                {t("add") || "Add"}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              {t("objectives_hint") ||
                "Press Enter or click Add to include each objective"}
            </p>

            {/* Existing Objectives List */}
            {objectives.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={objectives.map((_, i) => `objective-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="p-3 space-y-2 border border-gray-200 rounded-md bg-gray-50">
                    {objectives.map((objective, index) => (
                      <SortableObjectiveItem
                        key={`objective-${index}`}
                        id={`objective-${index}`}
                        objective={objective}
                        index={index}
                        onRemove={handleRemoveObjective}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        <SheetFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim()}
            className="bg-primary hover:bg-primary/90"
          >
            {t("save") || "Save"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ActivityMetadataSheet;
